interface iMeasuredProps {
    /** Absent means the collection could not measure this. It does not mean zero. */
    value?: number;
    format?: (value: number) => string;
    /**
     * Explains what "not measured" means at this call site.
     *
     * Defaults to the per-tenant reading, which is correct for a table body
     * cell: one row, one tenant, so "this tenant could not be read" is
     * literally true. It is NOT true at an aggregate call site — a footer
     * total or a summary tile spans every tenant, and the Behind tile's
     * absence in phase 1 means "no trend history yet", not a read failure.
     * Callers over an aggregate must supply their own accurate title.
     *
     * Also applied to a measured value's span when supplied, so an aggregate
     * caller (e.g. a footer total) can title a real number with the coverage
     * it represents.
     */
    title?: string;
    /**
     * Explains the ABSENT case only, and is never applied to a measured value.
     *
     * `title` above is deliberately applied to both branches, because an
     * aggregate caller wants to title a real number with the coverage it
     * represents. A per-cell caller usually wants the opposite: copy that
     * names why THIS value is missing, which would be actively false on a
     * value that is present. Passing an absent-only explanation as `title`
     * puts "did not report" on numbers that did.
     *
     * When both are supplied, `absentTitle` wins for the absent branch and
     * `title` still titles the measured one.
     */
    absentTitle?: string;
}

const DEFAULT_ABSENT_TITLE = "Not measured — this tenant could not be read";

/**
 * Renders one numeric cell, keeping absent and zero visibly distinct.
 *
 * An absent aggregate means the tenant could not be read; a zero means it was
 * read and measured zero. Rendering absent as "0" turns a tenant nobody can
 * reach into a perfectly healthy idle one — the exact failure this feature
 * exists to prevent. See contract §2.1.
 */
function Measured({ value, format, title, absentTitle }: iMeasuredProps) {
    if (value === undefined) {
        return (
            <span
                className="mp-absent"
                title={absentTitle ?? title ?? DEFAULT_ABSENT_TITLE}
            >
                —
            </span>
        );
    }

    return (
        <span
            className={value === 0 ? "mp-zero" : "mp-num"}
            title={title}
        >
            {format ? format(value) : value.toLocaleString()}
        </span>
    );
}

export default Measured;
